import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import Nav from "@/components/fyllr/Nav";
import Footer from "@/components/fyllr/Footer";
import Home from "@/pages/Home";
import LiquidIntro from "@/components/fyllr/LiquidIntro";
import Landing from "@/pages/Landing";
import BookingConfirmed from "@/pages/BookingConfirmed";
import SalonLogin from "@/pages/SalonLogin";
import SalonVerify from "@/pages/SalonVerify";
import SalonAdmin from "@/pages/SalonAdmin";
import SalonOnboard from "@/pages/SalonOnboard";
import SalonBillingReturn from "@/pages/SalonBillingReturn";
import WaitlistThanks from "@/pages/WaitlistThanks";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import PartnerAgreement from "@/pages/PartnerAgreement";
import Preferences from "@/pages/Preferences";
import Marketplace from "@/pages/Marketplace";
import MyBookings from "@/pages/MyBookings";
import Pricing from "@/pages/Pricing";
import ForSalons from "@/pages/ForSalons";
import ClientJoin from "@/pages/ClientJoin";
import AdminDashboard from "@/pages/AdminDashboard";
import Login from "@/pages/Login";
import ReferDashboard from "@/pages/ReferDashboard";
import SeoLanding from "@/pages/SeoLanding";
import EditorialGuide from "@/pages/EditorialGuide";
import { ClientAuthProvider } from "@/context/ClientAuth";
import MetricoolTracker from "@/components/fyllr/MetricoolTracker";

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
        return;
      }
    }
    window.scrollTo({ top: 0 });
  }, [pathname, hash]);
  return null;
}

function Shell() {
  const { pathname } = useLocation();
  // Marketplace is live at /. Landing (waitlist-only) kept at /waitlist.
  const isHome = pathname === "/";
  const standalone = pathname === "/join" || pathname === "/waitlist";
  const hideChrome = pathname === "/" || standalone;

  return (
    <>
      {pathname === "/" && <LiquidIntro />}
      {!standalone && <Nav overHero={isHome} />}
      <main>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/waitlist" element={<Landing />} />
          <Route path="/booking/confirmed" element={<BookingConfirmed />} />
          <Route path="/waitlist/thanks" element={<WaitlistThanks />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/partner-agreement" element={<PartnerAgreement />} />
          <Route path="/preferences" element={<Preferences />} />
          <Route path="/sessions" element={<Navigate to="/for-salons" replace />} />
          <Route path="/marketplace" element={<Marketplace />} />
          <Route path="/guides/melbourne/:guideSlug" element={<EditorialGuide />} />
          <Route path="/discover/melbourne/:timeSlug" element={<SeoLanding melbourneIntent />} />
          <Route path="/discover/:categorySlug/:suburbSlug/:timeSlug" element={<SeoLanding />} />
          <Route path="/discover/:categorySlug/:suburbSlug" element={<SeoLanding />} />
          <Route path="/my-bookings" element={<MyBookings />} />
          <Route path="/bookings" element={<Navigate to="/my-bookings" replace />} />
          <Route path="/login" element={<Login />} />
          <Route path="/admin/login/*" element={<SalonLogin admin />} />
          <Route path="/salon/login/*" element={<SalonLogin />} />
          <Route path="/salon/verify" element={<SalonVerify />} />
          <Route path="/salon/admin" element={<SalonAdmin />} />
          <Route path="/salon/onboard" element={<SalonOnboard />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/for-salons" element={<ForSalons />} />
          <Route path="/join" element={<ClientJoin />} />
          <Route path="/refer" element={<ReferDashboard />} />
          <Route path="/refer/dashboard" element={<ReferDashboard />} />
          <Route path="/signup" element={<Navigate to="/join" replace />} />
          <Route path="/salon/billing/success" element={<SalonBillingReturn />} />
          <Route path="/salon/billing/cancel" element={<SalonBillingReturn />} />
          <Route path="/partners" element={<SalonOnboard />} />
          <Route path="/partner" element={<Navigate to="/partners" replace />} />
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      {!hideChrome && <Footer />}
    </>
  );
}

export default function App() {
  return (
    <div className="App" data-testid="fyllr-app">
      <BrowserRouter>
        <ScrollToTop />
        <ClientAuthProvider>
          <MetricoolTracker />
          <Shell />
        </ClientAuthProvider>
        <Toaster
          position="bottom-center"
          toastOptions={{
            style: {
              background: "#3B0B0B",
              color: "#F6F0E8",
              border: "1px solid #9B3A35",
              borderRadius: 999,
              fontFamily: "'Inter Tight', sans-serif",
              fontSize: 13,
              letterSpacing: "0.04em",
            },
          }}
        />
      </BrowserRouter>
    </div>
  );
}
